import { z } from 'zod';
import type { TFunction } from 'i18next';

export const createSurveySchema = (t: TFunction) =>
  z
    .object({
      team: z.number().min(1, { message: t('surveyCreation.validation.team') }),
      name: z.string().min(1, { message: t('surveyCreation.validation.name') }),
      surveyType: z.enum(['psychological-safety', 'work-environment', 'custom'], {
        errorMap: () => ({ message: t('surveyCreation.validation.surveyType') }),
      }),
      psychologicalSafetyTemplateId: z.number().optional(),
      workEnvironmentTemplateId: z.number().optional(),
      customTemplateId: z.number().optional(),
      isPlanning: z.boolean(),
      isRepeating: z.boolean(),
      date: z.string().optional(),
      time: z.string().optional(),
      frequency: z.string().optional(),
      deadlineInDays: z
        .number()
        .min(1, { message: t('surveyCreation.validation.deadline') })
        .refine((value) => !isNaN(Number(value)), {
          message: t('surveyCreation.validation.deadlineNumber'),
        }),
      actionProposalDeadlineInDays: z
        .number()
        .min(1, { message: t('surveyCreation.validation.actionProposalDeadline') })
        .optional(),
      actionVotingDeadlineInDays: z
        .number()
        .min(1, { message: t('surveyCreation.validation.actionVotingDeadline') })
        .optional(),
      daysBeforeDeadlineToNotify: z
        .number()
        .min(0, { message: t('surveyCreation.validation.reminder') })
        .refine((value) => !isNaN(Number(value)), {
          message: t('surveyCreation.validation.reminderNumber'),
        }),
    })
    .superRefine((data, ctx) => {
      if (data.surveyType === 'psychological-safety') {
        if (!data.psychologicalSafetyTemplateId) {
          ctx.addIssue({
            path: ['psychologicalSafetyTemplateId'],
            message: t('surveyCreation.validation.psychologicalSafetyTemplate'),
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (data.surveyType === 'work-environment') {
        if (!data.workEnvironmentTemplateId) {
          ctx.addIssue({
            path: ['workEnvironmentTemplateId'],
            message: t('surveyCreation.validation.workEnvironmentTemplate'),
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (data.surveyType === 'custom') {
        if (!data.customTemplateId) {
          ctx.addIssue({
            path: ['customTemplateId'],
            message: t('surveyCreation.validation.customTemplate'),
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (data.isPlanning) {
        if (!data.date) {
          ctx.addIssue({
            path: ['date'],
            message: t('surveyCreation.validation.planningDate'),
            code: z.ZodIssueCode.custom,
          });
        }
        if (!data.time) {
          ctx.addIssue({
            path: ['time'],
            message: t('surveyCreation.validation.planningTime'),
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (data.isPlanning && data.isRepeating) {
        if (!data.frequency) {
          ctx.addIssue({
            path: ['frequency'],
            message: t('surveyCreation.validation.planningFrequency'),
            code: z.ZodIssueCode.custom,
          });
        }
      }
    });
